import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useSubscription } from '../../context/SubscriptionContext';

interface AuthLayoutProps {
  children: React.ReactNode;
}

const AuthLayout: React.FC<AuthLayoutProps> = ({ children }) => {
  const { isAuthenticated, user } = useAuth();
  const { hasActiveSubscription, checkSubscriptionStatus } = useSubscription();
  const [isChecking, setIsChecking] = useState(true);

  // Check subscription status when component mounts
  useEffect(() => {
    const verifySubscription = async () => {
      if (isAuthenticated && user?.token) {
        setIsChecking(true);
        try {
          // Check if we need to force a subscription check (after subscription creation)
          const forceCheck = localStorage.getItem('subscription_created') === 'true';
          if (forceCheck) {
            console.log('Subscription was just created, forcing status check');
            localStorage.removeItem('subscription_created');
          }
          
          // Check subscription status
          await checkSubscriptionStatus(forceCheck);
        } catch (error) {
          console.error('Error checking subscription status:', error);
        } finally {
          setIsChecking(false);
        }
      } else {
        setIsChecking(false);
      }
    };

    verifySubscription();
  }, [isAuthenticated, user, checkSubscriptionStatus]);

  // Show loading state while checking
  if (isChecking) {
    return (
      <div className="flex items-center justify-center h-screen bg-black">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4 text-white">Verifying subscription...</h2>
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto"></div>
        </div>
      </div>
    );
  }

  // Redirect if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  // Redirect if no active subscription
  if (!hasActiveSubscription) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

export default AuthLayout; 